import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, SimpleChanges } from '@angular/core';
import { SharedModule } from '@/app/shared/shared.module';
import { TranslateModule } from '@ngx-translate/core';
import { CreateInquiryCommand, InquiryProxy, InventoryProxy } from '@/app/proxy/proxies';
import { DescriptionMaxLength, InquiryKeys, InquiryTypes } from '@/app/shared/consts/comms-consts';
import { SelectItem } from 'primeng/api';
import { ChatPanelComponent } from './chat-panel/chat-panel.component';
import { FormGroup, FormControl, Validators } from '@angular/forms';
import { UserService } from '@/app/core/services/user.service';
import { SkeletonComponent } from '@/app/features/shared/components/skeleton/skeleton.component';
import { finalize } from 'rxjs';

export enum ChatPanelTypes {
  CreateTechnicalSupportInquiry = 'create_technical_support_inquiry',
  Chat = 'chat',
  Welcome = 'welcome',
}
@Component({
  selector: 'dk-support-chats',
  imports: [SharedModule, TranslateModule, ChatPanelComponent, SkeletonComponent],
  templateUrl: './chats.component.html',
  styleUrl: './chats.component.less',
  providers: [InquiryProxy, InventoryProxy],
})
export class ChatsComponent implements OnInit {
  @Input() chatPanelType: ChatPanelTypes = ChatPanelTypes.Welcome;
  @Input() inquiryId: string | undefined = undefined;
  @Input() currentInquiryKey: InquiryKeys = InquiryKeys.TechnicalSupport;

  @Output() chatCreated = new EventEmitter<string>();

  loadingDevices: boolean = false;
  creatingInquiry: boolean = false;

  // Custom skeletons are now defined inline in template
  // No need for generic skeleton configs anymore

  createTechnicalSupportInquiryForm: FormGroup = new FormGroup({
    selectedCategory: new FormControl('', [Validators.required]),
    selectedDevices: new FormControl(''),
    description: new FormControl('', [Validators.required, Validators.minLength(2)]),
  });

  categories = [
    { key: 'device', name: 'doktar_app.common.device_label' },
    { key: 'mobile_app', name: 'doktar_app.common.mobile_app_label' },
    { key: 'web', name: 'doktar_app.common.web_platform_label' },
  ];
  categoriesKeyToPlatformValueMap = {
    device: 'Device',
    mobile_app: 'Mobile App',
    web: 'Web',
  };
  devices: SelectItem[] = [];

  descriptionMessageSize = DescriptionMaxLength;

  constructor(
    private _inquiryProxy: InquiryProxy,
    private _inventoryProxy: InventoryProxy,
    private userService: UserService,
    private _cd: ChangeDetectorRef
  ) {
    this.setupFormValidation();
  }

  ngOnInit() {
    this.loadDevices();
  }

  setupFormValidation(): void {
    // Subscribe to category changes to update devices validation
    this.createTechnicalSupportInquiryForm.get('selectedCategory')?.valueChanges.subscribe(() => {
      this.updateDevicesValidation();
    });

    // Set initial validation state
    this.updateDevicesValidation();
  }

  updateDevicesValidation(): void {
    const selectedDevicesControl = this.createTechnicalSupportInquiryForm.get('selectedDevices');
    const selectedCategory = this.createTechnicalSupportInquiryForm.get('selectedCategory')?.value;

    if (selectedCategory?.key === 'device') {
      selectedDevicesControl?.setValidators([Validators.required]);
    } else {
      selectedDevicesControl?.clearValidators();
      selectedDevicesControl?.setValue(''); // Clear the value when not required
    }
    selectedDevicesControl?.updateValueAndValidity();
  }

  ngOnChanges(changes: SimpleChanges): void {
    let needsChangeDetection = false;

    if (changes['inquiryId']) {
      if (changes['inquiryId'].currentValue == undefined) {
        this.chatPanelType = ChatPanelTypes.Welcome;
        needsChangeDetection = true;
      }

      if (changes['inquiryId'].currentValue != undefined && this.chatPanelType === ChatPanelTypes.Chat) {
        this.loadDevices();
      }

      if (
        changes['inquiryId'].currentValue == undefined &&
        this.chatPanelType === ChatPanelTypes.CreateTechnicalSupportInquiry
      ) {
        this.createTechnicalSupportInquiryForm.reset();
        needsChangeDetection = true;
      }
    }

    if (changes['currentInquiryKey']) {
      this.chatPanelType = ChatPanelTypes.Welcome;
      if (this.createTechnicalSupportInquiryForm) {
        this.createTechnicalSupportInquiryForm.reset();
      }

      needsChangeDetection = true;
    }

    if (needsChangeDetection) {
      this._cd.detectChanges();
    }
  }

  loadDevices() {
    this.loadingDevices = true;
    this.devices = [];
    this._inventoryProxy.inventory_GetUserInventory(this.userService.currentUser.id).subscribe({
      next: (res) => {
        res.result.forEach((device) =>
          this.devices.push({
            label: device.name + ' (' + device.productionId + ')',
            value: device.id,
          })
        );
        this.loadingDevices = false;
        this._cd.detectChanges();
      },
      error: () => {
        this.loadingDevices = false;
      },
    });
  }

  createTechnicalSupportInquiry() {
    this.chatPanelType = ChatPanelTypes.CreateTechnicalSupportInquiry;
    this.loadDevices();
  }

  createTechnicalSupportInquiryWithDetails() {
    if (!this.createTechnicalSupportInquiryForm.valid) {
      return;
    }

    this.creatingInquiry = true;
    let createCommand: CreateInquiryCommand = new CreateInquiryCommand();
    createCommand.userId = this.userService.currentUser?.id;
    createCommand.inquiryTypeId = InquiryTypes.find((type) => type.key === this.currentInquiryKey)?.id ?? '';
    createCommand.inquiryContent = this.createTechnicalSupportInquiryForm.get('description')?.value;
    createCommand.inventoryId = this.createTechnicalSupportInquiryForm.get('selectedDevices')?.value == ""
      ? undefined
      : this.createTechnicalSupportInquiryForm.get('selectedDevices')?.value;
    createCommand.platform =
      this.categoriesKeyToPlatformValueMap[
        this.createTechnicalSupportInquiryForm.get('selectedCategory')?.value
          ?.key as keyof typeof this.categoriesKeyToPlatformValueMap
      ] ?? '';
    this._inquiryProxy.inquiry_Create(createCommand)
    .pipe(finalize(() => {
      this.creatingInquiry = false;
    }))
    .subscribe({
      next: (res) => {
        this.chatPanelType = ChatPanelTypes.Chat;
        this.inquiryId = res.result;
        this.chatCreated.emit(this.inquiryId);
      },
      error: () => {
        this.creatingInquiry = false;
      },
    });
  }

  cancelCreateInquiry() {
    this.chatPanelType = ChatPanelTypes.Welcome;
    this.createTechnicalSupportInquiryForm.reset();
  }

  get isDeviceCategorySelected(): boolean {
    return this.createTechnicalSupportInquiryForm.get('selectedCategory')?.value?.key === this.categories[0].key;
  }

  get isTechnicalSupportWelcome(): boolean {
    return this.chatPanelType === ChatPanelTypes.Welcome && this.currentInquiryKey === InquiryKeys.TechnicalSupport;
  }

  get isCreatingTechnicalSupportInquiry(): boolean {
    return this.chatPanelType === ChatPanelTypes.CreateTechnicalSupportInquiry;
  }

  get isChatOpen(): boolean {
    return this.chatPanelType === ChatPanelTypes.Chat;
  }
}
